import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import SpeciesGbifSearchField from './SpeciesGbifSearchField';
import { fieldValue, RANKS } from '../utils/speciesCorrectionTables';
/**
 * Adds (or edits) one entry of a SpeciesCorrectionsPage table - the same
 * shape as the exhibitions' own edit modals (ToxicityEditModal...): the
 * page itself only lists.
 *
 * gbifId-shaped fields search by name via SpeciesGbifSearchField instead
 * of a raw number input - a typo'd id used to be indistinguishable from a
 * real one until something broke elsewhere on the site; picking from a
 * real GBIF match rules that out. A field another one fills (`fillField`,
 * e.g. the scientific name that comes with the species picked) gets no
 * input of its own: it'd only be overwritten by that pick.
 */
export default class SpeciesCorrectionModal extends Modal {
  form = {};
  // Each search field's starting text: the row's own companion name.
  queries = {};
  loading = false;
  errorMessage = null;
  oninit(vnode) {
    super.oninit(vnode);
    const {
      table,
      row
    } = this.attrs;
    this.form = {};
    this.queries = {};
    if (row) {
      table.fields.filter(field => field.kind !== 'readonly').forEach(field => {
        this.form[field.key] = fieldValue(row, field.key);
        if (field.kind === 'gbifSearch' && field.fillField) {
          this.queries[field.key] = fieldValue(row, field.fillField);
        }
      });
    }
  }
  className() {
    return 'Modal--small SpeciesCorrectionModal';
  }
  title() {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    return [app.translator.trans(this.attrs.table.titleKey), ' - ', this.attrs.row ? t('editing_title') : t('add_title')];
  }
  content() {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    const {
      table
    } = this.attrs;
    return m("div", {
      className: "Modal-body"
    }, m("form", {
      className: "Form",
      onsubmit: e => this.submit(e)
    }, this.errorMessage && m("p", {
      className: "SpeciesCorrectionModal-error"
    }, this.errorMessage), table.fields.filter(field => this.isEditable(field)).map(field => this.field(field)), m("div", {
      className: "Form-group"
    }, m(Button, {
      type: "submit",
      className: "Button Button--primary Button--block",
      loading: this.loading
    }, this.attrs.row ? t('save_button') : t('add_button')))));
  }
  isEditable(field) {
    return field.kind !== 'readonly' && !this.attrs.table.fields.some(f => f.fillField === field.key);
  }
  field(field) {
    const label = app.translator.trans(field.labelKey);
    if (field.kind === 'gbifSearch') {
      return m("div", {
        className: "Form-group",
        key: field.key
      }, m("label", {
        className: "label"
      }, label), m(SpeciesGbifSearchField, {
        initialQuery: this.queries[field.key] || '',
        placeholder: app.translator.trans('champis-species.forum.search.placeholder'),
        required: true,
        onselect: species => {
          this.form[field.key] = species.id;
          if (field.fillField) {
            this.form[field.fillField] = species.scientificName || '';
          }
          m.redraw();
        },
        onclear: () => {
          this.form[field.key] = '';
        }
      }));
    }
    if (field.kind === 'rank') {
      return m("div", {
        className: "Form-group",
        key: field.key
      }, m("label", {
        className: "label"
      }, label), m("select", {
        className: "FormControl",
        value: this.form[field.key] || '',
        onchange: e => this.form[field.key] = e.target.value,
        required: true
      }, m("option", {
        value: "",
        disabled: true
      }, app.translator.trans('champis-species.forum.page.rank_placeholder')), RANKS.map(rank => m("option", {
        value: rank
      }, app.translator.trans(`champis-species.forum.page.ranks.${rank}`)))));
    }
    return m("div", {
      className: "Form-group",
      key: field.key
    }, m("label", {
      className: "label"
    }, label), m("input", {
      className: "FormControl",
      type: field.kind === 'number' ? 'number' : 'text',
      value: this.form[field.key] || '',
      oninput: e => this.form[field.key] = e.target.value,
      required: true
    }));
  }
  submit(e) {
    e.preventDefault();
    const {
      table,
      row
    } = this.attrs;
    const attributes = {};

    // Every non-readonly field, filled ones included (see isEditable()).
    table.fields.filter(field => field.kind !== 'readonly').forEach(field => {
      attributes[field.key] = field.kind === 'number' || field.kind === 'gbifSearch' ? parseInt(this.form[field.key], 10) : this.form[field.key];
    });
    const body = {
      data: {
        type: table.type,
        attributes
      }
    };
    if (row) {
      body.data.id = row.id();
    }
    this.loading = true;
    this.errorMessage = null;
    app.request({
      method: row ? 'PATCH' : 'POST',
      url: app.forum.attribute('apiUrl') + '/' + table.type + (row ? '/' + row.id() : ''),
      body,
      // Said in the modal itself, not as an alert behind it.
      errorHandler: () => {}
    }).then(() => {
      this.hide();
      this.attrs.onsaved();
    }).catch(error => {
      this.loading = false;
      this.errorMessage = error.response?.errors?.[0]?.detail || app.translator.trans('champis-species.forum.page.error');
      m.redraw();
    });
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesCorrectionModal', SpeciesCorrectionModal);